import { useState } from 'react';
import cx from 'classnames';

import SwapOpenIcon from '@mui/icons-material/SwapHoriz';
import SwapCloseIcon from '@mui/icons-material/Close';
import ExpandOpenIcon from '@mui/icons-material/ExpandMore';
import ExpandCloseIcon from '@mui/icons-material/ExpandLess';
import ListItemButton from '@mui/material/ListItemButton';
import ListItem from '@mui/material/ListItem';
import {
  ListItemSecondaryAction,
  IconButton,
  Collapse,
  Tooltip,
  List,
  Popover,
} from '@mui/material';

import { makeStyles } from '@mui/styles';

import SelectableItemList, { SelectableItemLabel } from '../SelectableItemList';

import { BEHAVIOR, HEIGHT, VARIANT } from './constants';

const useStyles = makeStyles(
  ({ spacing }) => ({
    buttonIcon: {
      pointerEvents: 'none',
    },
    buttonContainer: {
      paddingTop: spacing(2),
      paddingBottom: spacing(2),
    },
    itemContainer: {
      width: '100%',
      position: 'relative',
      top: spacing(-1),
    },
    compactContainer: {
      maxHeight: 240,
    },
    tooltipChild: {
      width: '100%',
      paddingRight: spacing(4),
    },
  }),
  { classNamePrefix: 'ContextSwitcher' }
);

export default function ContextSwitcher({
  beforeNode = null,
  afterNode = null,
  selectedItem = null,
  items = [],
  pinnedItems = [],
  variant = VARIANT.SWAP,
  behavior = BEHAVIOR.POPIN,
  onChange = () => {},
  onOpen = () => {},
  onClose = /* istanbul ignore next */ () => {},
  onOpened = /* istanbul ignore next */ () => {},
  onClosed = /* istanbul ignore next */ () => {},
  size = HEIGHT.UNSET,
  listComponent: ListComponent = List,
  ariaLabelClose = 'Close',
}) {
  const [isOpen, toggleOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const openSwitcher = (target) => {
    setAnchorEl(target);
    toggleOpen(true);
    onOpen();
  };

  const closeSwitcher = () => {
    setAnchorEl(null);
    toggleOpen(false);
    onClose();
  };

  const onButtonClick = (e) => {
    if (isOpen) {
      closeSwitcher();
    } else {
      openSwitcher(e.currentTarget);
    }
  };

  const onItemSelected = (item) => {
    onChange(item);
    closeSwitcher();
  };

  const classes = useStyles();

  const selectedItemLabel = selectedItem?.label;
  const selectedItemValue = selectedItem?.value;

  const variantToIcons = {
    swap: {
      open: <SwapOpenIcon />,
      close: <SwapCloseIcon />,
    },
    expand: {
      open: <ExpandOpenIcon />,
      close: <ExpandCloseIcon />,
    },
  };

  const icons = variantToIcons[variant];

  const pinnedItemList = Boolean(pinnedItems.length) && (
    <SelectableItemList
      items={pinnedItems}
      listComponent={ListComponent}
      selectedItemValue={selectedItemValue}
      onChange={onItemSelected}
      size={size}
    />
  );

  const itemList = Boolean(items.length) && (
    <SelectableItemList
      items={items}
      listComponent={ListComponent}
      selectedItemValue={selectedItemValue}
      onChange={onItemSelected}
      size={size}
    >
      {afterNode}
    </SelectableItemList>
  );

  const children = (
    <>
      {beforeNode}
      {pinnedItemList}
      {itemList}
    </>
  );

  return (
    <ListComponent
      className={cx({
        [classes.compactContainer]: size === HEIGHT.COMPACT,
      })}
    >
      <ListItemButton component="li" onClick={onButtonClick} className={classes.buttonContainer}>
        <Tooltip title={!selectedItemLabel ? '' : selectedItemLabel} enterDelay={500}>
          <div className={classes.tooltipChild}>
            <SelectableItemLabel variant="h6">{selectedItemLabel || '...'}</SelectableItemLabel>
          </div>
        </Tooltip>
        <ListItemSecondaryAction className={classes.buttonIcon}>
          <IconButton
            edge="end"
            size="small"
            color="inherit"
            tabIndex={-1}
            aria-label={isOpen && ariaLabelClose}
          >
            {isOpen ? icons.close : icons.open}
          </IconButton>
        </ListItemSecondaryAction>
      </ListItemButton>

      {behavior === BEHAVIOR.PUSH ? (
        <Collapse
          in={isOpen}
          component={ListItem}
          disableGutters
          classes={{ wrapper: classes.itemContainer }}
          unmountOnExit
          data-testid={BEHAVIOR.PUSH}
          onEntered={onOpened}
          onExited={onClosed}
        >
          {children}
        </Collapse>
      ) : (
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={closeSwitcher}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          data-testid={BEHAVIOR.POPIN}
          TransitionProps={{
            unmountOnExit: true,
            onEntered: onOpened,
            onExited: onClosed,
          }}
        >
          {children}
        </Popover>
      )}
    </ListComponent>
  );
}
